import React, { useEffect, useMemo, useState } from 'react';
import { useStorageBox } from '../StorageBoxContext';
import DriveItemIcon from './DriveItemIcon';
import {
  buildStorageBoxEditorSaveLocationPath,
  ellipsizeMiddle,
} from '../utils/storageBoxEditorSaveLocation';

function readPathMaxLength() {
  if (typeof window === 'undefined') return 52;
  const w = window.innerWidth;
  if (w < 400) return 22;
  if (w < 560) return 32;
  if (w < 768) return 42;
  return 56;
}

export function useStorageBoxEditorSaveLocationPath() {
  const {
    browseMode,
    breadcrumbs,
    lockRootItemId,
    activeSharedShare,
    readOnly,
    activeLabel,
    activeColorTag,
  } = useStorageBox();

  return useMemo(
    () =>
      buildStorageBoxEditorSaveLocationPath({
        browseMode,
        breadcrumbs,
        lockRootItemId,
        activeSharedShare,
        readOnly,
        activeLabel,
        activeColorTag,
      }),
    [
      browseMode,
      breadcrumbs,
      lockRootItemId,
      activeSharedShare,
      readOnly,
      activeLabel,
      activeColorTag,
    ]
  );
}

/** Folder icon for the save location (matches drive folder icons). */
export function useStorageBoxEditorSaveLocationFolderItem() {
  const { breadcrumbs } = useStorageBox();
  return useMemo(() => {
    const crumbs = breadcrumbs || [];
    const last = crumbs[crumbs.length - 1];
    if (last) {
      return {
        type: 'folder',
        name: last.name,
        folderColor: last.folderColor,
      };
    }
    return { type: 'folder', name: '' };
  }, [breadcrumbs]);
}

/**
 * File icon matching Storage Box drive (mime + name), or pass full `item`.
 * @param {{ type?: string, mimeType?: string, name?: string } | null} [item]
 * @param {'rich' | 'plain'} [kind]
 */
export function StorageBoxTextEditorFileIcon({
  item = null,
  mimeType,
  name,
  kind = 'plain',
  className = '',
}) {
  const driveItem = useMemo(() => {
    if (item?.type === 'file') return item;
    const resolvedName =
      name || (kind === 'rich' ? 'Untitled document.html' : 'Untitled.txt');
    const resolvedMime =
      mimeType || (kind === 'rich' ? 'text/html;charset=utf-8' : 'text/plain;charset=utf-8');
    return {
      type: 'file',
      mimeType: resolvedMime,
      name: resolvedName,
    };
  }, [item, mimeType, name, kind]);

  return (
    <DriveItemIcon
      item={driveItem}
      className={['sb-text-editor-modal__drive-icon', className].filter(Boolean).join(' ')}
    />
  );
}

/** Save location row (no Storage Box context required). */
export function TextEditorSaveLocation({ path, folderItem, className = '' }) {
  const [maxLength, setMaxLength] = useState(readPathMaxLength);

  useEffect(() => {
    const onResize = () => setMaxLength(readPathMaxLength());
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const displayPath = useMemo(
    () => ellipsizeMiddle(path, maxLength),
    [path, maxLength]
  );

  if (!path) return null;

  const folder =
    folderItem ||
    ({
      type: 'folder',
      name: 'Documents',
      folderColor: 'documents',
    });

  return (
    <span
      className={['sb-text-editor-modal__location-wrap', className].filter(Boolean).join(' ')}
      title={`Save location: ${path}`}
    >
      <DriveItemIcon
        item={folder}
        className="sb-text-editor-modal__drive-icon sb-text-editor-modal__drive-icon--location"
      />
      <span className="sb-text-editor-modal__location">{displayPath}</span>
    </span>
  );
}

export default function StorageBoxTextEditorLocation({ path: pathOverride, className = '' }) {
  const contextPath = useStorageBoxEditorSaveLocationPath();
  const folderItem = useStorageBoxEditorSaveLocationFolderItem();
  const fullPath = pathOverride ?? contextPath;

  if (!fullPath) return null;

  return (
    <TextEditorSaveLocation path={fullPath} folderItem={folderItem} className={className} />
  );
}
